(function () {
  'use strict';

  if (customElements.get('jb-picnic-drawer')) return;

  var STATUS_LABELS = {
    in_stock: 'In stock',
    preorder: 'Pre-order',
    out_of_stock: 'Out of stock'
  };

  function clean(value) {
    return String(value || '').trim();
  }

  function colourKey(value) {
    var key = clean(value).toLowerCase();
    return key === 'lucky' ? 'lucky star' : key;
  }

  function sameValue(left, right) {
    return clean(left).toLowerCase() === clean(right).toLowerCase();
  }

  class JellybeanPicnicDrawer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.dataset.initialised = 'true';

      this.sectionRoot = document.getElementById('jb-product-' + this.dataset.sectionId) || this.closest('[data-section-id]');
      this.dialog = this.querySelector('[data-jb-drawer]');
      this.openButton = this.querySelector('[data-jb-drawer-open]');
      this.closeButton = this.querySelector('[data-jb-drawer-close]');
      this.doneButton = this.querySelector('[data-jb-drawer-done]');
      this.inlineMode = clean(this.dataset.selectorLayout) === 'inline';
      this.defaultPicnicVariant = this.dataset.defaultPicnicVariant === 'true';
      this.cards = Array.from(this.querySelectorAll('[data-jb-colour-card]'));
      this.controls = Array.from(this.querySelectorAll('[data-jb-control]'));
      this.collectionControls = Array.from(this.querySelectorAll('[data-jb-collection-control]'));
      this.groupSections = Array.from(this.querySelectorAll('[data-jb-group-section]'));
      this.rails = Array.from(this.querySelectorAll('[data-jb-rail]'));
      this.previewZoom = this.querySelector('[data-jb-preview-zoom]');
      this.imagePreview = this.querySelector('[data-jb-image-preview]');
      this.imagePreviewImage = this.querySelector('[data-jb-image-preview-image]');
      this.imagePreviewCaption = this.querySelector('[data-jb-image-preview-caption]');
      this.imagePreviewClose = this.querySelector('[data-jb-image-preview-close]');
      this.choices = Array.from(this.querySelectorAll('[data-jb-choice]')).map(function (node) {
        return {
          node: node,
          group: clean(node.dataset.group),
          colour: clean(node.dataset.colour),
          fabric: clean(node.dataset.fabric),
          size: clean(node.dataset.size),
          variantId: clean(node.dataset.variantId),
          productId: clean(node.dataset.productId),
          productHandle: clean(node.dataset.productHandle),
          url: clean(node.dataset.url),
          status: clean(node.dataset.status) || 'out_of_stock',
          available: node.dataset.available === 'true',
          image: clean(node.dataset.image),
          price: clean(node.dataset.price)
        };
      });

      this.currentProductId = clean(this.dataset.currentProductId);
      this.state = {
        colour: clean(this.dataset.selectedColour),
        fabric: clean(this.dataset.selectedFabric) || 'Cotton',
        size: clean(this.dataset.selectedSize) || 'Regular'
      };
      var transferParams = new URL(window.location.href).searchParams;
      var requestedState = {
        colour: clean(transferParams.get('jb_colour')),
        fabric: clean(transferParams.get('jb_fabric')),
        size: this.state.size
      };
      var transferredChoice = requestedState.colour && requestedState.fabric
        ? this.findChoice(requestedState, this.currentProductId)
        : null;
      if (transferredChoice) this.state = requestedState;

      this.committedChoice = transferredChoice || this.findChoice(this.state, this.currentProductId) || this.findChoice(this.state);
      this.activeGroup = clean(this.dataset.selectedGroup) || (this.committedChoice && this.committedChoice.group) || 'picnic';
      var hasExplicitVariant = transferParams.has('variant');
      var preserveExplicitVariant = hasExplicitVariant && !this.defaultPicnicVariant;

      if (transferredChoice) {
        this.activeGroup = transferredChoice.group || this.activeGroup;
      } else if (preserveExplicitVariant && this.committedChoice) {
        // Preserve intentional links to a particular variant and show the
        // collection that actually contains it.
        this.activeGroup = this.committedChoice.group || this.activeGroup;
      } else if (!this.committedChoice || !sameValue(this.committedChoice.group, this.activeGroup)) {
        // A normal product-page visit should select from the collection shown
        // as active, rather than highlighting Picnic while an Original colour
        // remains selected and the purchase button appears sold out.
        var initialGroupChoice = this.findChoiceInGroup({
          colour: '',
          fabric: this.state.fabric,
          size: this.state.size
        }, this.activeGroup);
        if (initialGroupChoice) {
          this.committedChoice = initialGroupChoice;
          this.state = {
            colour: initialGroupChoice.colour,
            fabric: initialGroupChoice.fabric,
            size: initialGroupChoice.size
          };
          this.dataset.selectedColour = initialGroupChoice.colour;
          this.dataset.selectedFabric = initialGroupChoice.fabric;
          this.dataset.selectedSize = initialGroupChoice.size;
          this.dataset.selectedProductId = initialGroupChoice.productId;
        }
      }
      this.pendingChoice = this.committedChoice;
      var self = this;

      this.bindEvents();
      this.bindPurchaseForms();
      this.renderControls();
      this.renderCards(this.state);
      this.renderCollections();
      if (this.committedChoice) {
        this.renderSummary(this.committedChoice, false);
        this.applyChoiceToProduct(this.committedChoice);
      }
      window.requestAnimationFrame(function () {
        self.syncRails(true);
      });
    }

    bindEvents() {
      var self = this;

      if (this.openButton) {
        this.openButton.addEventListener('click', function () {
          self.openDrawer();
        });
      }

      if (this.closeButton) {
        this.closeButton.addEventListener('click', function () {
          self.closeDrawer(false);
        });
      }

      if (this.doneButton) {
        this.doneButton.addEventListener('click', function () {
          if (!self.pendingChoice || !self.isPurchasable(self.pendingChoice)) return;
          self.commitChoice(self.pendingChoice);
        });
      }

      this.cards.forEach(function (card) {
        card.addEventListener('click', function () {
          if (card.hidden) return;
          self.markRailInteracted(card.closest('[data-jb-rail]'));
          var nextState = {
            colour: card.dataset.colour,
            fabric: self.state.fabric,
            size: self.state.size
          };
          var choice = self.findChoice(nextState);
          if (!choice) return;
          if (self.inlineMode) {
            self.commitChoice(choice, true);
            return;
          }
          self.pendingChoice = choice;
          self.renderCards(nextState);
          self.renderSummary(choice, true);
        });
      });

      this.collectionControls.forEach(function (control) {
        control.addEventListener('click', function () {
          self.activeGroup = clean(control.dataset.group) || 'picnic';
          self.renderCollections();
          window.requestAnimationFrame(function () {
            self.syncRails(true);
          });
        });
      });

      this.rails.forEach(function (rail) {
        var section = rail.closest('[data-jb-group-section]');
        var previous = section && section.querySelector('[data-jb-rail-prev]');
        var next = section && section.querySelector('[data-jb-rail-next]');
        var ticking = false;

        rail.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            self.updateRailState(rail);
            ticking = false;
          });
        }, { passive: true });
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (eventName) {
          rail.addEventListener(eventName, function () {
            self.markRailInteracted(rail);
          }, { passive: eventName !== 'keydown' });
        });
        if (previous) {
          previous.addEventListener('click', function () {
            self.scrollRail(rail, -1);
          });
        }
        if (next) {
          next.addEventListener('click', function () {
            self.scrollRail(rail, 1);
          });
        }
      });

      if (this.previewZoom) {
        this.previewZoom.addEventListener('click', function () {
          self.openImagePreview();
        });
      }
      if (this.imagePreviewClose) {
        this.imagePreviewClose.addEventListener('click', function () {
          self.closeImagePreview();
        });
      }
      if (this.imagePreview) {
        this.imagePreview.addEventListener('click', function (event) {
          if (event.target === self.imagePreview) self.closeImagePreview();
        });
        this.imagePreview.addEventListener('close', function () {
          document.documentElement.classList.remove('jb-picnic-image-preview-open');
        });
      }

      window.addEventListener('resize', function () {
        window.requestAnimationFrame(function () {
          self.syncRails(false);
        });
      }, { passive: true });

      this.controls.forEach(function (control) {
        control.addEventListener('click', function () {
          var nextState = {
            colour: self.state.colour,
            fabric: self.state.fabric,
            size: self.state.size
          };
          nextState[control.dataset.jbControl] = control.dataset.value;
          var choice;
          if (control.dataset.jbControl === 'fabric') {
            // Collection cards act as the customer's browsing context. When the
            // page initially represents an Original colour but Picnic is open,
            // keep the fabric switch inside Picnic instead of jumping groups.
            choice = self.findChoiceInGroup(nextState, self.activeGroup);
          } else {
            choice = self.findChoice(nextState, self.currentProductId) || self.findChoice(nextState);
          }
          if (!choice) {
            if (control.dataset.jbControl === 'size') self.navigateToSizeFallback(nextState);
            return;
          }
          self.commitChoice(choice, true);
        });
      });

      if (this.dialog) {
        this.dialog.addEventListener('click', function (event) {
          if (event.target === self.dialog) self.closeDrawer(false);
        });
        this.dialog.addEventListener('close', function () {
          self.pendingChoice = self.committedChoice;
          self.renderCards(self.state);
          if (self.committedChoice) self.renderSummary(self.committedChoice, true);
        });
      }
    }

    bindPurchaseForms() {
      if (!this.inlineMode) return;
      var self = this;
      var root = this.sectionRoot || this;
      root.querySelectorAll('form').forEach(function (form) {
        if (!form.querySelector('[data-jb-purchase-variant]') || form.dataset.jbPicnicSubmitBound === 'true') return;
        form.dataset.jbPicnicSubmitBound = 'true';
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          var button = event.submitter || form.querySelector('button[type="submit"]');
          if (!button || button.getAttribute('aria-disabled') === 'true') return;
          self.addSelectedToCart(form, button);
        });
      });
    }

    addSelectedToCart(form, button) {
      if (form.dataset.jbPicnicAdding === 'true') return;
      form.dataset.jbPicnicAdding = 'true';

      var self = this;
      var choice = this.committedChoice;
      var formData = new FormData(form);
      if (choice && choice.variantId) formData.set('id', choice.variantId);
      var cartCountElement = document.querySelector('[data-cart-count]');
      var originalCartCount = cartCountElement
        ? parseInt(cartCountElement.getAttribute('data-cart-count') || cartCountElement.textContent, 10) || 0
        : 0;
      var requestedQuantity = parseInt(formData.get('quantity'), 10) || 1;
      var optimisticCartCount = originalCartCount + requestedQuantity;
      var originalText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding\u2026';

      // Confirm the click immediately. The full cart is reconciled from Shopify
      // after the add succeeds, and this count is rolled back if it fails.
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        bubbles: true,
        detail: { cartCount: optimisticCartCount }
      }));

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: formData
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (error) {
              throw new Error(error.description || 'Unable to add this item.');
            });
          }
          return response.json();
        })
        .then(function (item) {
          button.textContent = 'Added';
          // One Broadcast refresh updates the drawer, totals and authoritative
          // count. Avoid the previous duplicate events and extra /cart.js call.
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
        })
        .catch(function (error) {
          document.dispatchEvent(new CustomEvent('theme:cart:change', {
            bubbles: true,
            detail: { cartCount: originalCartCount }
          }));
          button.textContent = error.message || 'Please try again';
          window.setTimeout(function () {
            button.textContent = originalText;
          }, 1800);
        })
        .finally(function () {
          form.dataset.jbPicnicAdding = 'false';
          button.removeAttribute('aria-busy');
          window.setTimeout(function () {
            self.updatePurchaseButtons(self.committedChoice);
          }, 900);
        });
    }

    findChoice(state, preferredProductId) {
      var matches = this.choices.filter(function (choice) {
        return colourKey(choice.colour) === colourKey(state.colour) &&
          sameValue(choice.fabric, state.fabric) &&
          sameValue(choice.size, state.size);
      });
      if (preferredProductId) {
        var preferred = matches.find(function (choice) {
          return choice.productId === String(preferredProductId);
        });
        if (preferred) return preferred;
      }
      return matches[0] || null;
    }

    findChoiceInGroup(state, group) {
      var self = this;
      var matches = this.choices.filter(function (choice) {
        return sameValue(choice.group, group) &&
          sameValue(choice.fabric, state.fabric) &&
          sameValue(choice.size, state.size);
      });
      var exact = matches.find(function (choice) {
        return colourKey(choice.colour) === colourKey(state.colour);
      });
      var purchasable = matches.find(function (choice) {
        return self.isPurchasable(choice);
      });
      return exact || purchasable || matches[0] || null;
    }

    isPurchasable(choice) {
      return Boolean(choice && choice.available && choice.status !== 'out_of_stock');
    }

    sizeLabel(size) {
      if (sameValue(size, 'Medium')) return clean(this.dataset.mediumLabel) || 'Medium';
      return clean(this.dataset.smallLabel) || 'Small';
    }

    navigateToSizeFallback(state) {
      var path = sameValue(state.size, 'Medium')
        ? clean(this.dataset.mediumUrl)
        : clean(this.dataset.smallUrl);
      if (!path) return false;

      var target = new URL(path, window.location.origin);
      target.searchParams.set('jb_colour', state.colour);
      target.searchParams.set('jb_fabric', state.fabric);
      window.location.assign(this.previewSafeUrl(target.toString()));
      return true;
    }

    openDrawer() {
      if (!this.dialog) return;
      this.pendingChoice = this.committedChoice;
      this.renderCards(this.state);
      if (this.pendingChoice) this.renderSummary(this.pendingChoice, true);
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('jb-picnic-drawer-open');
    }

    closeDrawer(keepPending) {
      if (!keepPending) this.pendingChoice = this.committedChoice;
      if (this.dialog && this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('jb-picnic-drawer-open');
      if (this.openButton) this.openButton.focus({ preventScroll: true });
    }

    markRailInteracted(rail) {
      if (!rail) return;
      var section = rail.closest('[data-jb-group-section]');
      if (section) section.classList.add('has-rail-interaction');
    }

    scrollRail(rail, direction) {
      if (!rail) return;
      this.markRailInteracted(rail);
      var distance = Math.max(rail.clientWidth * 0.82, 180) * direction;
      if (typeof rail.scrollBy === 'function') {
        rail.scrollBy({ left: distance, behavior: 'smooth' });
      } else {
        rail.scrollLeft += distance;
      }
    }

    updateRailState(rail) {
      if (!rail) return;
      var shell = rail.closest('[data-jb-rail-shell]');
      var section = rail.closest('[data-jb-group-section]');
      var maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      var atStart = rail.scrollLeft <= 3;
      var atEnd = rail.scrollLeft >= maximum - 3;
      if (shell) {
        shell.classList.toggle('is-at-start', atStart);
        shell.classList.toggle('is-at-end', atEnd);
      }
      if (section) {
        var previous = section.querySelector('[data-jb-rail-prev]');
        var next = section.querySelector('[data-jb-rail-next]');
        if (previous) previous.disabled = atStart;
        if (next) next.disabled = atEnd;
      }
    }

    syncRails(centerActive) {
      var self = this;
      this.rails.forEach(function (rail) {
        var section = rail.closest('[data-jb-group-section]');
        if (centerActive && section && !section.hidden) {
          var activeCard = rail.querySelector('.jb-picnic-drawer__colour.is-active:not([hidden])');
          var targetLeft = activeCard
            ? activeCard.offsetLeft - Math.max(0, (rail.clientWidth - activeCard.offsetWidth) / 2)
            : 0;
          if (typeof rail.scrollTo === 'function') {
            rail.scrollTo({ left: Math.max(0, targetLeft), behavior: 'auto' });
          } else {
            rail.scrollLeft = Math.max(0, targetLeft);
          }
        }
        window.requestAnimationFrame(function () {
          self.updateRailState(rail);
        });
      });
    }

    largeImageUrl(path) {
      if (!path) return '';
      try {
        var target = new URL(path, window.location.origin);
        target.searchParams.set('width', '1400');
        return target.toString();
      } catch (error) {
        return path;
      }
    }

    openImagePreview() {
      if (!this.imagePreview) return;
      var choice = this.pendingChoice || this.committedChoice;
      var selectedImage = this.querySelector('[data-jb-preview-image]');
      var imagePath = choice && choice.image ? choice.image : (selectedImage && selectedImage.src);
      if (!imagePath) return;

      if (this.imagePreviewImage) {
        this.imagePreviewImage.src = this.largeImageUrl(imagePath);
        this.imagePreviewImage.alt = choice ? choice.colour : '';
      }
      if (this.imagePreviewCaption && choice) {
        this.imagePreviewCaption.textContent = choice.colour + ' · ' + choice.fabric + ' · ' + this.sizeLabel(choice.size);
      }
      if (typeof this.imagePreview.showModal === 'function') {
        this.imagePreview.showModal();
      } else {
        this.imagePreview.setAttribute('open', '');
      }
      document.documentElement.classList.add('jb-picnic-image-preview-open');
    }

    closeImagePreview() {
      if (!this.imagePreview) return;
      if (this.imagePreview.open && typeof this.imagePreview.close === 'function') {
        this.imagePreview.close();
      } else {
        this.imagePreview.removeAttribute('open');
      }
      document.documentElement.classList.remove('jb-picnic-image-preview-open');
      if (this.previewZoom) this.previewZoom.focus({ preventScroll: true });
    }

    commitChoice(choice, fromControl) {
      if (!choice) return;
      var sizeChanged = !sameValue(choice.size, this.state.size);
      if (choice.productId !== this.currentProductId && (!this.inlineMode || sizeChanged)) {
        var targetUrl = new URL(this.previewSafeUrl(choice.url));
        targetUrl.searchParams.set('jb_colour', choice.colour);
        targetUrl.searchParams.set('jb_fabric', choice.fabric);
        window.location.assign(targetUrl.toString());
        return;
      }

      this.committedChoice = choice;
      this.pendingChoice = choice;
      this.state = {
        colour: choice.colour,
        fabric: choice.fabric,
        size: choice.size
      };
      this.activeGroup = choice.group || this.activeGroup;
      this.dataset.selectedColour = choice.colour;
      this.dataset.selectedFabric = choice.fabric;
      this.dataset.selectedSize = choice.size;
      this.dataset.selectedGroup = this.activeGroup;
      this.dataset.selectedProductId = choice.productId;

      this.applyChoiceToProduct(choice);
      this.renderControls();
      this.renderCards(this.state);
      this.renderSummary(choice, false);
      var self = this;
      window.requestAnimationFrame(function () {
        self.syncRails(true);
      });
      if (!fromControl) this.closeDrawer(true);
    }

    previewSafeUrl(path) {
      var target = new URL(path, window.location.origin);
      var current = new URL(window.location.href);
      ['preview_theme_id', 'country', '_fd', 'pb'].forEach(function (key) {
        if (current.searchParams.has(key)) target.searchParams.set(key, current.searchParams.get(key));
      });
      return target.toString();
    }

    applyChoiceToProduct(choice) {
      var root = this.sectionRoot || this;
      root.querySelectorAll('[data-jb-purchase-variant]').forEach(function (selector) {
        if (selector.tagName === 'SELECT' && !Array.from(selector.options).some(function (option) { return option.value === choice.variantId; })) {
          selector.add(new Option(choice.variantId, choice.variantId));
        }
        selector.value = choice.variantId;
        selector.setAttribute('value', choice.variantId);
        selector.setAttribute('data-selected-variant-id', choice.variantId);
        var form = selector.form;
        var productIdInput = form && form.querySelector('[name="product-id"]');
        if (productIdInput) productIdInput.value = choice.productId;
      });

      this.updatePurchaseButtons(choice);

      if (choice.price) {
        var price = root.querySelector('.jb-product__price');
        var stickyPrice = root.querySelector('.jb-product__sticky-price');
        if (price) price.textContent = choice.price;
        if (stickyPrice) stickyPrice.textContent = choice.price;
      }

      if (choice.image) {
        var mainImage = document.getElementById('jb-main-img-' + this.dataset.sectionId);
        if (mainImage) {
          mainImage.classList.add('is-fading');
          window.setTimeout(function () {
            mainImage.src = choice.image;
            mainImage.alt = choice.colour;
            mainImage.classList.remove('is-fading');
          }, 120);
        }
      }

      var pageUrl = this.inlineMode && choice.productId !== this.currentProductId && choice.url
        ? new URL(this.previewSafeUrl(choice.url))
        : new URL(window.location.href);
      pageUrl.searchParams.set('variant', choice.variantId);
      pageUrl.searchParams.delete('jb_colour');
      pageUrl.searchParams.delete('jb_fabric');
      window.history.replaceState({}, '', pageUrl.toString());
      document.dispatchEvent(new CustomEvent('jb:picnic-variant-changed', {
        detail: {
          variantId: choice.variantId,
          productId: choice.productId,
          productHandle: choice.productHandle,
          colour: choice.colour,
          fabric: choice.fabric,
          size: choice.size,
          status: choice.status
        }
      }));
    }

    updatePurchaseButtons(choice) {
      var root = this.sectionRoot || this;
      var isUnavailable = !this.isPurchasable(choice);
      root.querySelectorAll('.jb-product__add-btn, .jb-product__sticky-btn[type="submit"]').forEach(function (button) {
        var text;
        if (isUnavailable) {
          text = button.dataset.textSoldout || 'Sold Out';
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        } else {
          text = choice.status === 'preorder'
            ? (button.dataset.textPreorder || 'Preorder Now')
            : (button.dataset.textInstock || button.dataset.textDefault || 'Add to Cart');
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
        button.textContent = text;
      });
    }

    renderControls() {
      var state = this.state;
      this.controls.forEach(function (control) {
        var selected = sameValue(state[control.dataset.jbControl], control.dataset.value);
        control.classList.toggle('is-selected', selected);
        control.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    renderCards(state) {
      var self = this;
      var counts = { picnic: 0, original: 0 };
      var firstChoiceByGroup = { picnic: null, original: null };
      this.cards.forEach(function (card) {
        var cardState = {
          colour: card.dataset.colour,
          fabric: state.fabric,
          size: state.size
        };
        var choice = self.findChoice(cardState);
        var hasChoice = Boolean(choice);
        var purchasable = self.isPurchasable(choice);
        var active = colourKey(card.dataset.colour) === colourKey((self.pendingChoice || self.committedChoice || {}).colour);
        var image = card.querySelector('[data-jb-card-image]');
        var status = card.querySelector('[data-jb-card-status]');

        card.hidden = !hasChoice;
        if (hasChoice) {
          counts[card.dataset.group] += 1;
          if (!firstChoiceByGroup[card.dataset.group]) firstChoiceByGroup[card.dataset.group] = choice;
        }
        card.disabled = !hasChoice;
        card.classList.toggle('is-soldout', hasChoice && !purchasable);
        card.classList.toggle('is-preorder', hasChoice && choice.status === 'preorder');
        card.classList.toggle('is-active', active && hasChoice);
        card.setAttribute('aria-pressed', active && hasChoice ? 'true' : 'false');
        card.setAttribute('aria-label', card.dataset.colour + (choice ? '. ' + (STATUS_LABELS[choice.status] || 'Out of stock') : '. Unavailable'));
        if (image && choice && choice.image) image.src = choice.image;
        if (choice) self.setStatus(status, choice.status, STATUS_LABELS[choice.status] || 'Out of stock');
      });

      Object.keys(counts).forEach(function (group) {
        self.querySelectorAll('[data-jb-count="' + group + '"]').forEach(function (countNode) {
          countNode.textContent = ' · ' + counts[group];
        });
        self.querySelectorAll('[data-jb-collection-count="' + group + '"]').forEach(function (countNode) {
          countNode.textContent = counts[group];
        });
        var collectionImage = self.querySelector('[data-jb-collection-image="' + group + '"]');
        var imageChoice = firstChoiceByGroup[group];
        if (collectionImage && imageChoice && imageChoice.image) collectionImage.src = imageChoice.image;
      });
      this.groupCounts = counts;
      if (!counts[this.activeGroup]) {
        this.activeGroup = counts.original ? 'original' : 'picnic';
      }
      this.renderCollections();
    }

    renderCollections() {
      if (!this.collectionControls.length) return;
      var activeGroup = this.activeGroup || 'picnic';
      var counts = this.groupCounts || {};
      this.collectionControls.forEach(function (control) {
        var group = clean(control.dataset.group);
        var available = Number(counts[group] || 0) > 0;
        var selected = available && group === activeGroup;
        control.hidden = !available;
        control.classList.toggle('is-selected', selected);
        control.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.groupSections.forEach(function (section) {
        var group = clean(section.dataset.group);
        section.hidden = Number(counts[group] || 0) === 0 || group !== activeGroup;
      });
    }

    renderSummary(choice, previewOnly) {
      if (!choice) return;
      var label = STATUS_LABELS[choice.status] || 'Out of stock';
      var triggerImage = this.querySelector('[data-jb-trigger-image]');
      var triggerColour = this.querySelector('[data-jb-trigger-colour]');
      var triggerDetail = this.querySelector('[data-jb-trigger-detail]');
      var triggerStatus = this.querySelector('[data-jb-trigger-status]');
      var previewImage = this.querySelector('[data-jb-preview-image]');
      var previewColour = this.querySelector('[data-jb-preview-colour]');
      var previewDetail = this.querySelector('[data-jb-preview-detail]');
      var previewStatus = this.querySelector('[data-jb-preview-status]');

      if (previewImage && choice.image) previewImage.src = choice.image;
      if (this.previewZoom) {
        var zoomLabel = clean(this.previewZoom.dataset.zoomLabel) || 'Enlarge selected colour image';
        this.previewZoom.setAttribute('aria-label', zoomLabel + ': ' + choice.colour);
      }
      if (previewColour) previewColour.textContent = choice.colour;
      if (previewDetail) previewDetail.textContent = choice.fabric + ' · ' + this.sizeLabel(choice.size);
      this.setStatus(previewStatus, choice.status, label);

      if (!previewOnly) {
        if (triggerImage && choice.image) triggerImage.src = choice.image;
        if (triggerColour) triggerColour.textContent = choice.colour;
        if (triggerDetail) triggerDetail.textContent = choice.fabric + ' · ' + this.sizeLabel(choice.size);
        this.setStatus(triggerStatus, choice.status, label);
      }

      if (this.doneButton) {
        this.doneButton.disabled = !this.isPurchasable(choice);
        this.doneButton.setAttribute('aria-disabled', this.doneButton.disabled ? 'true' : 'false');
      }
    }

    setStatus(node, status, label) {
      if (!node) return;
      node.classList.remove('jb-picnic-status--in_stock', 'jb-picnic-status--preorder', 'jb-picnic-status--out_of_stock');
      node.classList.add('jb-picnic-status--' + status);
      node.textContent = label;
    }
  }

  customElements.define('jb-picnic-drawer', JellybeanPicnicDrawer);
})();
